<template>
    <div class="flex flex-col gap-10">
        <div v-if="showPerspectiveAssessmentReminder" class="rounded-2xl border border-dashed border-border-strong bg-surface-sunken p-6 body text-ink-muted">
            {{ t("profile.perspective.archetypeTab.assessmentReminder") }}
        </div>
        <div v-else-if="!hasPerspectiveReport" class="flex justify-center">
            <PerspectiveIntroCard
                track-intro-funnel
                :is-checking-assessment-status="isFetchingAssessment"
                :is-starting="isStartingAssessment"
                :has-in-progress-session="assessmentStatus?.status === 'in_progress'"
                :error-message="startAssessmentErrorMessage"
                @continue="handleContinueAssessment"
                @start="handleStart" />
        </div>

        <template v-else-if="perspectiveReport">
            <div>
                <div class="flex items-center justify-between mb-1">
                    <h3 class="scaffold-body-small">{{ t("profile.perspective.archetypeTab.yourPerspectiveHeading") }}</h3>
                    <BaseButton
                        v-if="isPerspectiveAssessmentEnabled"
                        theme="neutral"
                        size="sm"
                        class="inline-flex items-center gap-2"
                        :disabled="isStartingAssessment"
                        @click="handleStart">
                        <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                        {{ t("profile.perspective.archetypeTab.retakeAssessment") }}
                    </BaseButton>
                </div>
                <YourPerspective :perspective-report="perspectiveReport" />
            </div>
            <div v-if="lensDimensions">
                <h3 class="scaffold-body-small mb-1">{{ t("profile.perspective.archetypeTab.dimensionDeepDiveHeading") }}</h3>
                <DimensionSection :dimensions="lensDimensions" />
            </div>
            <div v-if="perspectiveReport?.mbti_letters && strengths.length > 0">
                <h3 class="scaffold-body-small mb-1">{{ t("profile.perspective.archetypeTab.strengthsHeading") }}</h3>
                <div class="grid md:grid-cols-3 gap-4">
                    <PerspectiveCard
                        v-for="(strength, index) in strengths"
                        :key="strength.title"
                        :title="strength.title"
                        :body="strength.body"
                        :letters="perspectiveReport.mbti_letters"
                        :index="index"
                        category="strength">
                        <template #icon>
                            <img :src="cardIcons[index]" :alt="t('profile.perspective.archetypeTab.strengthIconAlt')" class="size-7.5" />
                        </template>
                    </PerspectiveCard>
                </div>
            </div>
            <div v-if="perspectiveReport?.mbti_letters && growthAreas.length > 0">
                <h3 class="scaffold-body-small mb-1">{{ t("profile.perspective.archetypeTab.growthAreasHeading") }}</h3>
                <div class="grid md:grid-cols-3 gap-4">
                    <PerspectiveCard
                        v-for="(growthArea, index) in growthAreas"
                        :key="growthArea.title"
                        :title="growthArea.title"
                        :body="growthArea.body"
                        :letters="perspectiveReport.mbti_letters"
                        :index="index"
                        category="growth_area">
                        <template #icon>
                            <img :src="cardIcons[index]" :alt="t('profile.perspective.archetypeTab.growthAreaIconAlt')" class="size-7.5 grayscale" />
                        </template>
                    </PerspectiveCard>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import decisionMakingIcon from "~img/perspective/decision-making.svg";
import energyIcon from "~img/perspective/energy.svg";
import infoProcessingIcon from "~img/perspective/information-processing.svg";
import type { PerspectiveReport } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import { usePerspective } from "~vue/Perspective/composable/usePerspective";
import YourPerspective from "~vue/UserProfile/YourPerspective.vue";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import DimensionSection from "./DimensionSection.vue";
import PerspectiveCard from "./PerspectiveCard.vue";
import PerspectiveIntroCard from "./PerspectiveIntroCard.vue";

type ArchetypeTabProps = {
    perspectiveReport?: PerspectiveReport | null;
    perspectiveReportEnabled?: boolean;
};

const { perspectiveReport, perspectiveReportEnabled = false } = defineProps<ArchetypeTabProps>();

const { t } = useI18n();
const page = usePage();

const hasPerspectiveReport = computed(() => Boolean(perspectiveReport));
// Placeholder results (archetype set by an admin, trial seeding) have dimensions but no lens
// scores, and the deep dive has nothing to show for them (BUG-803, BUG-1231).
const lensDimensions = computed(() => {
    const dimensions = perspectiveReport?.dimensions;
    return dimensions?.some((dimension) => dimension.lenses?.length) ? dimensions : null;
});

const showPerspectiveAssessmentReminder = computed(() => perspectiveReportEnabled && !hasPerspectiveReport.value && !page.props.perspective_assessment_enabled);

const isPerspectiveAssessmentEnabled = computed(() => page.props.perspective_assessment_enabled);

const { handleFetchAssessment, handleStartAssessment, handleContinueAssessment, assessmentStatus, isStartingAssessment, isFetchingAssessment, startAssessmentErrorMessage } =
    usePerspective({ source: "user_profile" });

const handleStart = () => {
    // perspectiveReportEnabled checks for both assessment (nadia-native) and report (tt) feature flags.
    // perspective assessments enabled is just for the nadia native; if that option exists prefer this one
    if (isPerspectiveAssessmentEnabled.value) {
        handleStartAssessment();
    } else if (perspectiveReportEnabled) {
        globalThis.open(`${page.props.align_host}/perspective/questions`, "_blank");
    }
};

onMounted(() => {
    if (isPerspectiveAssessmentEnabled.value) {
        handleFetchAssessment();
    }
});

const strengths = computed(() => {
    if (perspectiveReport?.mbti_letters) {
        return PERSPECTIVE_TYPES[perspectiveReport.mbti_letters]?.strengths ?? [];
    }
    return [];
});

const growthAreas = computed(() => {
    if (perspectiveReport?.mbti_letters) {
        return PERSPECTIVE_TYPES[perspectiveReport.mbti_letters]?.challenges ?? [];
    }
    return [];
});

const cardIcons = [energyIcon, infoProcessingIcon, decisionMakingIcon];
</script>
